// ============================================================
// ShiftPicker — 點格子後跳出的班別選單
// ------------------------------------------------------------
// 【為什麼要有這個】
// 舊版是「油漆桶」：先在上方選一個班別，再去點格子。點下去的結果取決於
// 上方選了什麼——同班別＝取消、不同班別＝替換、沒選＝刪除。規則藏在
// 看不見的狀態裡，所以「要取消一格全天班，得先回上方點回全天班再點格子」。
//
// 改為：點格子 → 就地列出所有班別 ＋「清除排班」，點一下完成。
// 沒有模式、沒有前置步驟，看到什麼就是什麼。
//
// 版面：
//   平板／電腦 → 浮動選單，貼在被點的格子旁（用 portal 掛到 body，
//                避免被排班表的 overflow 裁掉）
//   手機       → 底部抽屜，拇指容易搆到
// ============================================================

import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ShiftTemplate } from '../stores/scheduleStore';

interface Props {
  staffName: string;
  dayLabel: string;
  templates: ShiftTemplate[];
  /** 這一格目前的班別 id（沒有排班則為 null） */
  currentTemplateId: string | null;
  /** 這一格是否已有排班（有才顯示「清除排班」） */
  hasSchedule: boolean;
  /** 被點擊的格子位置，用於定位浮動選單 */
  anchor: DOMRect;
  onPick: (t: ShiftTemplate) => void;
  onClear: () => void;
  onClose: () => void;
}

const PANEL_W = 248;
const MARGIN = 8;

export function ShiftPicker({
  staffName, dayLabel, templates, currentTemplateId, hasSchedule,
  anchor, onPick, onClear, onClose,
}: Props) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  // Esc 關閉
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // 浮動選單定位：預設在格子下方；靠近視窗邊緣時翻到上方／往內收
  useLayoutEffect(() => {
    if (isMobile) return;
    const h = panelRef.current?.offsetHeight ?? 320;
    let top = anchor.bottom + 6;
    if (top + h > window.innerHeight - MARGIN) {
      top = Math.max(MARGIN, anchor.top - h - 6);
    }
    let left = anchor.left;
    if (left + PANEL_W > window.innerWidth - MARGIN) {
      left = window.innerWidth - PANEL_W - MARGIN;
    }
    setPos({ top, left: Math.max(MARGIN, left) });
  }, [anchor, isMobile]);

  const header = (
    <div className="px-4 pt-3 pb-2 border-b border-morandi-100">
      <div className="text-sm font-semibold text-morandi-800">{staffName}</div>
      <div className="text-xs text-morandi-400 mt-0.5">{dayLabel}</div>
    </div>
  );

  const list = (
    <div className="py-1.5 max-h-[50vh] md:max-h-[46vh] overflow-y-auto">
      {templates.map(t => {
        const isRest = t.kind === 'REST_DAY' || t.kind === 'REGULAR_LEAVE';
        const icon = t.kind === 'REGULAR_LEAVE' ? '📌' : t.kind === 'REST_DAY' ? '💤' : null;
        const active = currentTemplateId === t.id;
        return (
          <button
            key={t.id}
            onClick={() => onPick(t)}
            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left transition-colors min-h-touch
              ${active ? 'bg-sage-50' : 'hover:bg-morandi-50'}`}
          >
            <span
              className="w-3 h-3 rounded-full flex-shrink-0 border border-black/5"
              style={{ backgroundColor: t.color }}
            />
            <span className="flex-1 min-w-0">
              <span className={`block text-sm truncate ${active ? 'font-semibold text-sage-700' : 'text-morandi-700'}`}>
                {icon && <span className="mr-1">{icon}</span>}{t.name}
              </span>
              {!isRest && (
                <span className="block text-xs font-mono text-morandi-400 mt-0.5">
                  {t.startTime}–{t.endTime}
                </span>
              )}
            </span>
            {active && <span className="text-sage-600 text-sm flex-shrink-0">✓</span>}
          </button>
        );
      })}
    </div>
  );

  const clearBtn = hasSchedule && (
    <div className="border-t border-morandi-100 p-2">
      <button
        onClick={onClear}
        className="w-full py-2.5 rounded-btn text-sm font-medium text-rose-600 hover:bg-rose-50 transition-colors min-h-touch"
      >
        清除排班
      </button>
    </div>
  );

  // ── 手機：底部抽屜 ──
  if (isMobile) {
    return createPortal(
      <div className="fixed inset-0 z-[60] bg-black/30 flex items-end" onClick={onClose}>
        <div
          className="w-full bg-white rounded-t-2xl shadow-2xl animate-slide-up"
          style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
          onClick={e => e.stopPropagation()}
        >
          <div className="w-10 h-1 bg-morandi-200 rounded-full mx-auto mt-2.5" />
          {header}
          {list}
          {clearBtn}
        </div>
      </div>,
      document.body
    );
  }

  // ── 平板／電腦：貼著格子的浮動選單 ──
  return createPortal(
    <>
      <div className="fixed inset-0 z-[60]" onClick={onClose} />
      <div
        ref={panelRef}
        className="fixed z-[61] bg-white rounded-card shadow-float border border-morandi-200 animate-fade-in"
        style={{
          width: PANEL_W,
          top: pos?.top ?? -9999,
          left: pos?.left ?? -9999,
          visibility: pos ? 'visible' : 'hidden',
        }}
        onClick={e => e.stopPropagation()}
      >
        {header}
        {list}
        {clearBtn}
      </div>
    </>,
    document.body
  );
}
